/**
 * "Migrate from OpenSolar" (`/migrate/opensolar`, company admins): Connect › Preview › Choose › Import › Report
 * (docs/features/migration-opensolar.md). The installer connects their own OpenSolar account with its org id and a
 * token they make with one command (or, as a separate option, their OpenSolar email and password, sent once and never
 * kept); a light worker reads the account (the preview), the admin chooses what to bring and reviews the team,
 * pricing and equipment, then the import runs in the background with its progress here, and the report lists every
 * item with its reason. Pure parts: lib/migration.ts.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CircleAlert, CircleCheck, Download, FileText, KeyRound, ListChecks, Plug, RefreshCw, ShieldCheck, Unplug, Users, Wallet } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link, useSearchParams } from "react-router-dom";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, CodeBlock, Empty, Field, PageHead, Segmented, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { StatusBadge } from "../components/StatusBadge";
import { useOrgPlan } from "./admin/OrgPlan";
import { useLocale, useT, type Translate } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import { formatLocale } from "../i18n/negotiate.ts";
import { useDates } from "../lib/useDates";
import {
  choicesBody,
  chosenCount,
  defaultChoices,
  estimateMessage,
  formatBytes,
  isWorking,
  ITEM_LABELS,
  progressShare,
  rewardKey,
  runTone,
  STEPS,
  stepOf,
  TOKEN_CURL,
  type Choices,
  type RunStatus,
  type Step,
} from "../lib/migration";
import "./migration.css";

type Run = S["MigrationRun"];

const b = (chunks: React.ReactNode[]) => <b>{chunks}</b>;
const code = (chunks: React.ReactNode[]) => <code className="i18n-ltr">{chunks}</code>;

/** A run's status as a chip. */
function RunBadge({ status }: { status: RunStatus }) {
  const t = useT();
  return <Badge tone={runTone(status)}>{t("integrations.migration.runStatus", { status })}</Badge>;
}

/** Sizes in the reader's locale. */
function useBytes(): (bytes: number | null | undefined) => string {
  const { locale } = useLocale();
  return (bytes) => formatBytes(bytes, formatLocale(locale));
}

/** The labels of the switch-bonus checks the backend sends (their English `label` for a check we do not know). */
const REWARD_CHECKS: Record<string, MessageKey> = {
  projects: "integrations.migration.check.projects",
  span: "integrations.migration.check.span",
  age: "integrations.migration.check.age",
  priced: "integrations.migration.check.priced",
  first: "integrations.migration.check.first",
};

function useRuns(orgId: string) {
  return useQuery({
    queryKey: ["migrations", orgId],
    enabled: !!orgId,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/migrations", { params: { path: { org_id: orgId } } })),
  });
}

function useRun(orgId: string, runId: string | null) {
  return useQuery({
    queryKey: ["migration", orgId, runId],
    enabled: !!runId,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/migrations/{run_id}", { params: { path: { org_id: orgId, run_id: runId! } } })),
    refetchInterval: (q) => (isWorking(q.state.data?.status) ? 2000 : false),
  });
}

function Stepper({ step }: { step: Step }) {
  const t = useT();
  const at = STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="mig-steps" aria-label={t("integrations.migration.steps")}>
      {STEPS.map((s, i) => (
        <li key={s.key} className={cx(i < at && "done", i === at && "current")} aria-current={i === at ? "step" : undefined}>
          <span className="mig-step-num">{i < at ? <CircleCheck size={14} /> : i + 1}</span>
          {t(s.label)}
        </li>
      ))}
    </ol>
  );
}

// ------------------------------------------------------------------------------------------------------- connect
function ConnectStep({ orgId, onConnected }: { orgId: string; onConnected: (run: Run) => void }) {
  const t = useT();
  const toast = useToast();
  const [method, setMethod] = useState<"token" | "password">("token");
  const [orgIdText, setOrgIdText] = useState("");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const connect = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/migrations/opensolar/connect", {
          params: { path: { org_id: orgId } },
          body: method === "token" ? { source_org_id: orgIdText.trim(), method, token: token.trim() } : { source_org_id: orgIdText.trim(), method, email: email.trim(), password },
        }),
      ),
    onSuccess: (run) => {
      setToken("");
      setPassword("");
      toast(t("integrations.migration.connectedTo", { account: run.source_account_name ?? "OpenSolar" }));
      onConnected(run);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const ready = orgIdText.trim() && (method === "token" ? token.trim() : email.trim() && password);
  return (
    <div className="split-wide">
      <Card title={t("integrations.migration.connect.title")} description={t("integrations.migration.connect.description")}>
        <Segmented
          value={method}
          onChange={setMethod}
          options={[
            { value: "token", label: t("integrations.migration.connect.methodToken") },
            { value: "password", label: t("integrations.migration.connect.methodPassword") },
          ]}
        />
        <form
          className="stack"
          style={{ marginTop: 14 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) connect.mutate();
          }}
        >
          <Field label={t("integrations.migration.connect.orgId")} hint={t("integrations.migration.connect.orgIdHint")}>
            <TextInput inputMode="numeric" dir="ltr" value={orgIdText} onChange={(e) => setOrgIdText(e.target.value)} placeholder={t("integrations.migration.connect.orgIdPlaceholder")} autoComplete="off" />
          </Field>
          {method === "token" ? (
            <>
              <Field label={t("integrations.migration.connect.token")} hint={t("integrations.migration.connect.tokenHint")}>
                <TextInput type="password" dir="ltr" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" placeholder={t("integrations.migration.connect.tokenPlaceholder")} />
              </Field>
              <div className="mig-howto">
                <b>
                  <KeyRound size={14} /> {t("integrations.migration.connect.howTitle")}
                </b>
                <p className="small muted">
                  <FormattedMessage id="integrations.migration.connect.howRun" values={{ code }} />
                </p>
                <CodeBlock code={TOKEN_CURL} />
                <p className="small muted">{t("integrations.migration.connect.machineUser")}</p>
              </div>
            </>
          ) : (
            <div className="mig-password">
              <Alert tone="warn" title={t("integrations.migration.connect.passwordTitle")}>
                {t("integrations.migration.connect.passwordBody")}
              </Alert>
              <Field label={t("integrations.migration.connect.email")}>
                <TextInput type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
              </Field>
              <Field label={t("integrations.migration.connect.password")}>
                <TextInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              </Field>
            </div>
          )}
          <div>
            <Button variant="primary" type="submit" icon={<Plug size={16} />} loading={connect.isPending} disabled={!ready}>
              {t("integrations.migration.connect.submit")}
            </Button>
          </div>
        </form>
      </Card>
      <div className="stack">
        <Card title={t("integrations.migration.before.title")}>
          <ul className="mig-bullets">
            <li><FormattedMessage id="integrations.migration.before.api" values={{ b }} /></li>
            <li><FormattedMessage id="integrations.migration.before.rawData" values={{ b }} /></li>
            <li>{t("integrations.migration.before.nothingYet")}</li>
          </ul>
        </Card>
        <Card title={t("integrations.migration.data.title")}>
          <ul className="mig-list">
            <li><ShieldCheck size={14} /> {t("integrations.migration.data.ownAccess")}</li>
            <li><ShieldCheck size={14} /> {t("integrations.migration.data.token")}</li>
            <li><ShieldCheck size={14} /> {t("integrations.migration.data.dropped")}</li>
            <li><ShieldCheck size={14} /> {t("integrations.migration.data.noEmails")}</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------- preview
function Count({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub muted small">{sub}</div>}
    </div>
  );
}

function PreviewSummary({ preview }: { preview: S["MigrationPreview"] }) {
  const t = useT();
  const intl = useIntl();
  const bytes = useBytes();
  const num = (n: number) => intl.formatNumber(n);
  const estimate = estimateMessage(preview.estimate);
  const components = preview.components ?? {};
  return (
    <div className="stack">
      <div className="grid-4 stats-tight">
        <Count
          label={t("integrations.migration.preview.projects")}
          value={num(preview.projects)}
          sub={preview.oldest_project ? intl.formatDateTimeRange(new Date(preview.oldest_project), new Date(preview.newest_project ?? preview.oldest_project), { year: "numeric", month: "short", day: "numeric" }) : undefined}
        />
        <Count label={t("integrations.migration.preview.contacts")} value={num(preview.contacts)} sub={t("integrations.migration.preview.contactsSub")} />
        <Count
          label={t("integrations.migration.preview.systems")}
          value={num(preview.systems)}
          sub={preview.projects_with_system != null ? t("integrations.migration.preview.systemsSub", { count: preview.projects_with_system }) : undefined}
        />
        <Count label={t("integrations.migration.preview.files")} value={num(preview.files)} sub={bytes(preview.files_bytes)} />
      </div>
      <div className="grid-4 stats-tight">
        <Count
          label={t("integrations.migration.preview.layouts")}
          value={preview.raw_data ? t("integrations.migration.preview.yes") : t("integrations.migration.preview.no")}
          sub={preview.raw_data ? t("integrations.migration.preview.layoutsKept") : t("integrations.migration.preview.layoutsNeed")}
        />
        <Count
          label={t("integrations.migration.preview.equipment")}
          value={num((components.module ?? 0) + (components.inverter ?? 0) + (components.battery ?? 0))}
          sub={t("integrations.migration.preview.equipmentSub", { modules: components.module ?? 0, inverters: components.inverter ?? 0, batteries: components.battery ?? 0 })}
        />
        <Count label={t("integrations.migration.preview.users")} value={num(preview.users)} sub={t("integrations.migration.preview.usersSub")} />
        <Count
          label={t("integrations.migration.preview.duplicates")}
          value={num(preview.duplicates.address + preview.duplicates.email)}
          sub={t("integrations.migration.preview.duplicatesSub", { address: preview.duplicates.address, email: preview.duplicates.email })}
        />
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        {estimate && `${t(estimate.key, estimate.values)} `}
        {t("integrations.migration.preview.pricing", { schemes: preview.pricing_schemes ?? 0, options: preview.payment_options ?? 0 })}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------- choose
function TeamReview({ orgId, runId }: { orgId: string; runId: string }) {
  const tr = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const team = useQuery({
    queryKey: ["migration-team", orgId, runId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/migrations/{run_id}/team", { params: { path: { org_id: orgId, run_id: runId } } })),
  });
  const [roles, setRoles] = useState<Record<string, S["AssignableRole"]>>({});
  const invite = useMutation({
    mutationFn: (p: S["MigrationTeamMember"]) =>
      unwrap(api.POST("/v1/orgs/{org_id}/invitations", { params: { path: { org_id: orgId } }, body: { email: p.email, role: roles[p.email] ?? p.suggested_role } })),
    onSuccess: (_d, p) => {
      toast(tr("integrations.migration.team.invited", { email: p.email }));
      void qc.invalidateQueries({ queryKey: ["migration-team", orgId, runId] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!team.data) return <Spinner label={tr("integrations.common.loading")} />;
  const t = team.data;
  return (
    <div className="stack">
      <p className="small muted" style={{ margin: 0 }}>
        {t.seats_limit != null
          ? tr("integrations.migration.team.intro", { used: t.seats_used, limit: t.seats_limit })
          : tr("integrations.migration.team.introUnlimited", { used: t.seats_used })}
      </p>
      {!t.people.length ? (
        <p className="muted">{tr("integrations.migration.team.none")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{tr("integrations.migration.team.col.person")}</th>
                <th>{tr("integrations.migration.team.col.role")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {t.people.map((p) => (
                <tr key={p.email}>
                  <td>
                    <div className="cell-main" dir="auto">{p.name ?? p.email}</div>
                    <div className="cell-sub">
                      <span dir="auto">{p.email}</span>
                      {p.job_title ? <> · <span dir="auto">{p.job_title}</span></> : null}
                      {p.sso_required ? ` · ${tr("integrations.migration.team.sso")}` : ""}
                    </div>
                  </td>
                  <td>
                    {p.state === "new" ? (
                      <select className="input" style={{ minWidth: 130 }} aria-label={tr("integrations.migration.team.roleFor", { email: p.email })} value={roles[p.email] ?? p.suggested_role} onChange={(e) => setRoles((r) => ({ ...r, [p.email]: e.target.value as S["AssignableRole"] }))}>
                        {(["admin", "designer", "sales", "viewer"] as const).map((r) => (
                          <option key={r} value={r}>
                            {tr(`shell.role.${r}`)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge tone={p.state === "member" ? "ok" : "info"}>{p.state === "member" ? tr("integrations.migration.team.member") : tr("integrations.migration.team.invitedBadge")}</Badge>
                    )}
                  </td>
                  <td className="num">
                    {p.state === "new" && (
                      <Button size="sm" loading={invite.isPending && invite.variables?.email === p.email} onClick={() => invite.mutate(p)}>
                        {tr("integrations.migration.team.invite")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PricingReview({ orgId, runId }: { orgId: string; runId: string }) {
  const t = useT();
  const pricing = useQuery({
    queryKey: ["migration-pricing", orgId, runId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/migrations/{run_id}/pricing", { params: { path: { org_id: orgId, run_id: runId } } })),
  });
  if (!pricing.data) return <Spinner label={t("integrations.common.loading")} />;
  const p = pricing.data;
  return (
    <div className="stack">
      <p className="small muted" style={{ margin: 0 }}>
        <FormattedMessage id="integrations.migration.pricing.intro" values={{ link: (c) => <Link to="/rules">{c}</Link> }} />
      </p>
      <div className="mig-two">
        <div>
          <h4>{t("integrations.migration.pricing.theirs")}</h4>
          {!p.schemes.length && !p.payment_options.length && <p className="muted">{t("integrations.migration.pricing.none")}</p>}
          {p.schemes.map((s) => (
            <div key={s.title} className="mig-config">
              <b dir="auto">{s.title}</b> <span className="muted small">{s.formula ?? ""}{s.auto_apply ? ` · ${t("integrations.migration.pricing.auto")}` : ""}</span>
              {s.configuration && <pre>{s.configuration}</pre>}
            </div>
          ))}
          {p.payment_options.map((o) => (
            <div key={o.title} className="mig-config">
              <b dir="auto">{o.title}</b> <span className="muted small">{o.payment_type ?? ""}</span>
              {o.configuration && <pre>{o.configuration}</pre>}
            </div>
          ))}
        </div>
        <div>
          <h4>{t("integrations.migration.pricing.ours")}</h4>
          <dl className="kv">
            {p.ours.map((r) => (
              <div key={r.label} style={{ display: "contents" }}>
                <dt>{r.label}</dt>
                <dd>{r.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

const MATCH_LABELS: Record<S["MigrationEquipmentMatch"], MessageKey> = {
  catalogue: "integrations.migration.equipment.matchCatalogue",
  cec: "integrations.migration.equipment.matchCec",
  none: "integrations.migration.equipment.matchNone",
};

function EquipmentReview({ orgId, runId }: { orgId: string; runId: string }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const key = ["migration-equipment", orgId, runId];
  const equipment = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/migrations/{run_id}/equipment", { params: { path: { org_id: orgId, run_id: runId } } })),
  });
  const [edits, setEdits] = useState<Record<string, { enable: boolean; unit_cost: string; unit_price: string }>>({});
  const items = equipment.data?.items ?? [];
  useEffect(() => {
    setEdits((current) => {
      const next = { ...current };
      for (const i of items)
        if (!next[i.source_id] && i.match !== "none")
          next[i.source_id] = { enable: true, unit_cost: String(i.unit_cost ?? i.their_cost ?? ""), unit_price: String(i.unit_price ?? "") };
      return next;
    });
  }, [equipment.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/migrations/{run_id}/equipment", {
          params: { path: { org_id: orgId, run_id: runId } },
          body: {
            items: Object.entries(edits)
              .filter(([, e]) => e.enable)
              .map(([source_id, e]) => ({ source_id, enable: true, unit_cost: e.unit_cost === "" ? null : Number(e.unit_cost), unit_price: e.unit_price === "" ? null : Number(e.unit_price) })),
          },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      toast(t("integrations.migration.equipment.enabledToast"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!equipment.data) return <Spinner label={t("integrations.common.loading")} />;
  const set = (id: string, patch: Partial<{ enable: boolean; unit_cost: string; unit_price: string }>) => setEdits((e) => ({ ...e, [id]: { ...e[id], ...patch } }));
  const chosen = Object.values(edits).filter((e) => e.enable).length;
  return (
    <div className="stack">
      <p className="small muted" style={{ margin: 0 }}>
        {t("integrations.migration.equipment.intro")}
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>OpenSolar</th>
              <th>{t("integrations.migration.equipment.col.match")}</th>
              <th className="num">{t("integrations.migration.equipment.col.cost")}</th>
              <th className="num">{t("integrations.migration.equipment.col.price")}</th>
              <th>{t("integrations.migration.equipment.col.enable")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const e = edits[i.source_id];
              return (
                <tr key={i.source_id}>
                  <td>
                    <div className="cell-main" dir="auto">{[i.manufacturer, i.model].filter(Boolean).join(" ")}</div>
                    <div className="cell-sub">
                      {t("integrations.migration.equipment.kind", { kind: i.kind })}
                      {i.is_default ? ` · ${t("integrations.migration.equipment.default")}` : ""}
                    </div>
                  </td>
                  <td>
                    <Badge tone={i.match === "none" ? undefined : i.enabled ? "ok" : "info"}>{i.enabled ? t("integrations.migration.equipment.enabled") : t(MATCH_LABELS[i.match])}</Badge>
                    {i.our_label && <div className="cell-sub" dir="auto">{i.our_label}</div>}
                    {i.match === "none" && i.reason && <div className="cell-sub" dir="auto">{i.reason}</div>}
                  </td>
                  <td className="num">
                    {e ? <input className="input mig-money" aria-label={t("integrations.migration.equipment.costOf", { model: i.model ?? "" })} inputMode="decimal" value={e.unit_cost} onChange={(ev) => set(i.source_id, { unit_cost: ev.target.value })} /> : <span className="faint">—</span>}
                  </td>
                  <td className="num">
                    {e ? <input className="input mig-money" aria-label={t("integrations.migration.equipment.priceOf", { model: i.model ?? "" })} inputMode="decimal" value={e.unit_price} onChange={(ev) => set(i.source_id, { unit_price: ev.target.value })} /> : <span className="faint">—</span>}
                  </td>
                  <td>{e ? <Switch checked={e.enable} onChange={(v) => set(i.source_id, { enable: v })} label={<span className="sr-only">{t("integrations.migration.equipment.enableOf", { model: i.model ?? "" })}</span>} /> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div>
        <Button icon={<ListChecks size={16} />} loading={apply.isPending} disabled={!chosen} onClick={() => apply.mutate()}>
          {t("integrations.migration.equipment.enableN", { count: chosen })}
        </Button>
      </div>
    </div>
  );
}

function ChooseStep({ orgId, run }: { orgId: string; run: Run }) {
  const t = useT();
  const bytes = useBytes();
  const toast = useToast();
  const qc = useQueryClient();
  const preview = run.preview!;
  const stages = preview.stages;
  const [choices, setChoices] = useState<Choices>(() => defaultChoices(stages));
  const [review, setReview] = useState<"team" | "pricing" | "equipment">("team");
  const allStages = stages.map((s) => s.stage);
  const selected = choices.stages.length ? choices.stages : allStages;
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/migrations/{run_id}/start", {
          params: { path: { org_id: orgId, run_id: run.id } },
          body: choicesBody({ ...choices, stages: selected }, allStages),
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(["migration", orgId, run.id], data);
      void qc.invalidateQueries({ queryKey: ["migrations", orgId] });
      toast(t("integrations.migration.choose.started"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const toggle = (stage: string, on: boolean) => setChoices((c) => ({ ...c, stages: on ? [...new Set([...selected, stage])] : selected.filter((s) => s !== stage) }));
  return (
    <div className="stack">
      <Card title={t("integrations.migration.choose.found", { account: run.source_account_name ?? "OpenSolar" })}>
        <PreviewSummary preview={preview} />
      </Card>
      <div className="stack">
        <Card title={t("integrations.migration.choose.title")} description={t("integrations.migration.choose.description")}>
          <div className="stack">
            <div>
              <div className="mig-label">{t("integrations.migration.choose.stages")}</div>
              <div className="mig-stages">
                {stages.map((s) => (
                  <label key={s.stage} className="mig-stage">
                    <input type="checkbox" checked={selected.includes(s.stage)} onChange={(e) => toggle(s.stage, e.target.checked)} />
                    <span>
                      <b dir="auto">{s.stage}</b> <span className="muted small">{t("integrations.migration.choose.stageMapsTo", { status: s.status })}</span>
                    </span>
                    <span className="mig-count">{s.count}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-grid">
              <Field label={t("integrations.migration.choose.from")}>
                <TextInput type="date" value={choices.created_from ?? ""} onChange={(e) => setChoices((c) => ({ ...c, created_from: e.target.value || null }))} />
              </Field>
              <Field label={t("integrations.migration.choose.until")}>
                <TextInput type="date" value={choices.created_to ?? ""} onChange={(e) => setChoices((c) => ({ ...c, created_to: e.target.value || null }))} />
              </Field>
            </div>
            <div className="mig-switches">
              <Switch checked={choices.only_with_system} onChange={(v) => setChoices((c) => ({ ...c, only_with_system: v }))} label={t("integrations.migration.choose.onlyWithSystem")} />
              <Switch checked={choices.skip_duplicates} onChange={(v) => setChoices((c) => ({ ...c, skip_duplicates: v }))} label={t("integrations.migration.choose.skipDuplicates")} />
              <Switch checked={choices.files} onChange={(v) => setChoices((c) => ({ ...c, files: v }))} label={t("integrations.migration.choose.files", { size: bytes(preview.files_bytes) })} />
            </div>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="muted small">{t("integrations.migration.choose.count", { count: chosenCount(stages, choices.stages.length ? choices.stages : []) })}</span>
              <Button variant="primary" icon={<DirIcon><ArrowRight size={16} /></DirIcon>} loading={start.isPending} disabled={!selected.length} onClick={() => start.mutate()}>
                {t("integrations.migration.choose.start")}
              </Button>
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              {t("integrations.migration.choose.layouts")}
            </p>
          </div>
        </Card>
        <Card title={t("integrations.migration.review.title")} description={t("integrations.migration.review.description")}>
          <div className="stack">
            <Segmented
              value={review}
              onChange={setReview}
              options={[
                { value: "team", label: t("integrations.migration.review.team") },
                { value: "pricing", label: t("integrations.migration.review.pricing") },
                { value: "equipment", label: t("integrations.migration.review.equipment") },
              ]}
            />
            {review === "team" && <TeamReview orgId={orgId} runId={run.id} />}
            {review === "pricing" && <PricingReview orgId={orgId} runId={run.id} />}
            {review === "equipment" && <EquipmentReview orgId={orgId} runId={run.id} />}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------- run and report
function Totals({ run }: { run: Run }) {
  const t = useT();
  const intl = useIntl();
  const num = (n: number) => intl.formatNumber(n);
  const p = run.totals.projects;
  const f = run.totals.files;
  return (
    <div className="grid-4 stats-tight">
      <Count label={t("integrations.migration.totals.imported")} value={num(p.imported + p.updated + p.partial)} sub={t("integrations.migration.totals.importedSub", { partial: p.partial, updated: p.updated })} />
      <Count label={t("integrations.migration.totals.unchanged")} value={num(p.unchanged)} sub={t("integrations.migration.totals.unchangedSub")} />
      <Count label={t("integrations.migration.totals.skipped")} value={num(p.skipped)} sub={t("integrations.migration.totals.skippedSub")} />
      <Count label={t("integrations.migration.totals.failed")} value={num(p.failed)} sub={t("integrations.migration.totals.filesSub", { kept: f.imported, not: f.skipped + f.failed })} />
    </div>
  );
}

function RunStep({ orgId, run, onDisconnect }: { orgId: string; run: Run; onDisconnect: () => void }) {
  const t = useT();
  const intl = useIntl();
  const share = progressShare(run);
  const p = run.progress;
  return (
    <Card title={t("integrations.migration.run.title")} actions={<RunBadge status={run.status} />}>
      <div className="stack">
        <div>
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
            <b>
              {p.files_total
                ? t("integrations.migration.run.progressFiles", { done: p.done, total: p.total, filesDone: p.files_done, filesTotal: p.files_total })
                : t("integrations.migration.run.progress", { done: p.done, total: p.total })}
            </b>
            <span className="muted small">{intl.formatNumber(share, { style: "percent" })}</span>
          </div>
          <div className="usage-bar" role="progressbar" aria-label={t("integrations.migration.run.progressLabel")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(share * 100)}>
            <span style={{ width: `${Math.round(share * 100)}%` }} />
          </div>
        </div>
        {p.waiting_reason && <Alert tone="info">{p.waiting_reason}.</Alert>}
        <Totals run={run} />
        <p className="small muted" style={{ margin: 0 }}>
          {t("integrations.migration.run.leave")}
        </p>
        <div>
          <DisconnectButton orgId={orgId} run={run} onDone={onDisconnect} />
        </div>
      </div>
    </Card>
  );
}

function DisconnectButton({ orgId, run, onDone }: { orgId: string; run: Run; onDone: () => void }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/migrations/{run_id}/disconnect", { params: { path: { org_id: orgId, run_id: run.id } } })),
    onSuccess: (data) => {
      qc.setQueryData(["migration", orgId, run.id], data);
      void qc.invalidateQueries({ queryKey: ["migrations", orgId] });
      toast(t("integrations.migration.disconnected"));
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!run.connected) return null;
  return (
    <Button variant="ghost" icon={<Unplug size={16} />} loading={disconnect.isPending} onClick={() => disconnect.mutate()}>
      {t("integrations.migration.disconnect")}
    </Button>
  );
}

function ReportStep({ orgId, run, onAgain }: { orgId: string; run: Run; onAgain: () => void }) {
  const t = useT();
  const dates = useDates();
  const intl = useIntl();
  const toast = useToast();
  const qc = useQueryClient();
  const plan = useOrgPlan(orgId, false);
  const [status, setStatus] = useState<"all" | "failed" | "skipped" | "partial">("all");
  const items = useQuery({
    queryKey: ["migration-items", orgId, run.id, status],
    queryFn: () =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/migrations/{run_id}/items", {
          params: { path: { org_id: orgId, run_id: run.id }, query: { limit: 200, status: status === "all" ? undefined : status } },
        }),
      ),
  });
  const retry = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/migrations/{run_id}/retry", { params: { path: { org_id: orgId, run_id: run.id } } })),
    onSuccess: (data) => {
      qc.setQueryData(["migration", orgId, run.id], data);
      toast(t("integrations.migration.report.retrying"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const csv = useMutation({
    mutationFn: async () => {
      const response = await rawFetch(`/api/v1/orgs/${orgId}/migrations/${run.id}/report.csv`);
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? "opensolar-import.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      return a.download;
    },
    onSuccess: (name) => toast(t("integrations.migration.report.downloaded", { name })),
    onError: (e) => toast(errorMessage(e), true),
  });
  const failed = run.totals.projects.failed + run.totals.files.failed;
  const badgeName = plan.data?.badge?.name ?? t("integrations.migration.report.badgeFallback");
  const reward = rewardKey(run.reward?.status);
  return (
    <div className="stack">
      {run.status === "failed" && run.error && (
        <Alert tone="danger" title={t("integrations.migration.report.stopped")}>
          {run.error}
        </Alert>
      )}
      <Card
        title={t("integrations.migration.report.title", { account: run.source_account_name ?? "OpenSolar" })}
        description={run.finished_at ? t("integrations.migration.report.finished", { date: dates.dateTime(run.finished_at) }) : undefined}
        actions={
          <div className="row">
            <Button icon={<Download size={16} />} loading={csv.isPending} onClick={() => csv.mutate()}>
              {t("integrations.migration.report.csv")}
            </Button>
            {failed > 0 && run.connected && (
              <Button icon={<RefreshCw size={16} />} loading={retry.isPending} onClick={() => retry.mutate()}>
                {t("integrations.migration.report.retry")}
              </Button>
            )}
            <Button variant="ghost" icon={<Plug size={16} />} onClick={onAgain}>
              {t("integrations.migration.report.again")}
            </Button>
          </div>
        }
      >
        <Totals run={run} />
      </Card>
      {run.reward && (
        <Card
          title={
            <span className="row" style={{ gap: 8 }}>
              <Wallet size={18} /> {t("integrations.migration.reward.title")}
              {run.reward.status === "approved" && <StatusBadge badge={plan.data?.badge} />}
            </span>
          }
        >
          {reward && <p style={{ marginTop: 0 }}>{t(reward, { badge: badgeName })}</p>}
          <ul className="mig-checks">
            {run.reward.checks.map((c) => (
              <li key={c.key} className={c.ok ? "ok" : "no"}>
                {c.ok ? <CircleCheck size={15} /> : <CircleAlert size={15} />} {REWARD_CHECKS[c.key] ? t(REWARD_CHECKS[c.key]) : c.label}
                {typeof c.value === "number" ? t("integrations.migration.reward.value", { value: intl.formatNumber(c.value), needed: intl.formatNumber(Number(c.needed ?? 0)) }) : ""}
              </li>
            ))}
          </ul>
          <Link to="/lux#switch" className="small">
            {t("integrations.migration.reward.see")}
          </Link>
        </Card>
      )}
      <Card
        pad={false}
        title={t("integrations.migration.report.items")}
        actions={
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: t("integrations.migration.report.all") },
              { value: "partial", label: t("integrations.migration.item.partial") },
              { value: "skipped", label: t("integrations.migration.item.skipped") },
              { value: "failed", label: t("integrations.migration.item.failed") },
            ]}
          />
        }
      >
        {!items.data ? (
          <Spinner label={t("integrations.common.loading")} />
        ) : !items.data.items.length ? (
          <Empty icon={<FileText />} title={t("integrations.migration.report.empty")} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("integrations.migration.report.col.item")}</th>
                  <th>{t("integrations.migration.report.col.result")}</th>
                  <th className="hide-sm">{t("integrations.migration.report.col.why")}</th>
                </tr>
              </thead>
              <tbody>
                {items.data.items.map((i) => {
                  const l = ITEM_LABELS[i.status];
                  return (
                    <tr key={i.id}>
                      <td>
                        <div className="cell-main ellipsis" dir="auto">{i.label ?? i.source_id}</div>
                        <div className="cell-sub">{t("integrations.migration.report.source", { type: i.source_type, id: i.source_id })}</div>
                      </td>
                      <td>
                        <Badge tone={l?.tone}>{l ? t(l.label) : i.status}</Badge>
                      </td>
                      <td className="hide-sm small muted" dir="auto">{i.reason ?? ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------- the page
export function Migration() {
  const t = useT();
  const dates = useDates();
  const orgId = useOrgId();
  const { can } = useOrg();
  const canEdit = can("integrations", "edit");
  const [params, setParams] = useSearchParams();
  const runs = useRuns(canEdit ? orgId : "");
  const [fresh, setFresh] = useState(false);
  const runId = params.get("run") ?? (fresh ? null : runs.data?.items.find((r) => r.status !== "cancelled")?.id ?? null);
  const run = useRun(orgId, canEdit ? runId : null);
  const qc = useQueryClient();
  const current = run.data ?? null;
  const step = fresh || !current ? "connect" : stepOf(current);
  const plan = useOrgPlan(orgId, false);
  const title = useMemo(
    () => (current?.source_account_name ? t("integrations.migration.page.titleAccount", { account: current.source_account_name }) : t("integrations.migration.page.title")),
    [current?.source_account_name, t],
  );

  if (!canEdit)
    return (
      <>
        <PageHead title={t("integrations.migration.page.title")} />
        <Alert tone="info">{t("integrations.migration.page.askAdmin")}</Alert>
      </>
    );
  if (runs.isLoading) return <Spinner label={t("integrations.common.loading")} />;
  const select = (id: string | null) => {
    setFresh(id === null);
    const next = new URLSearchParams(params);
    if (id) next.set("run", id);
    else next.delete("run");
    setParams(next, { replace: true });
  };

  return (
    <div className="migration">
      <PageHead
        title={title}
        description={t("integrations.migration.page.description")}
        actions={plan.data?.badge ? <StatusBadge badge={plan.data.badge} /> : undefined}
      />
      <Stepper step={step} />
      {step === "connect" && (
        <ConnectStep
          orgId={orgId}
          onConnected={(r) => {
            qc.setQueryData(["migration", orgId, r.id], r);
            void qc.invalidateQueries({ queryKey: ["migrations", orgId] });
            select(r.id);
          }}
        />
      )}
      {step === "preview" && current && (
        <Card title={t("integrations.migration.reading.title")} actions={<DisconnectButton orgId={orgId} run={current} onDone={() => select(null)} />}>
          <div className="row" style={{ gap: 12 }}>
            <Spinner label={t("integrations.common.loading")} />
            <div>
              <b>{t("integrations.migration.reading.connected", { account: current.source_account_name ?? "OpenSolar" })}</b>
              <div className="muted small">{t("integrations.migration.reading.body")}</div>
            </div>
          </div>
        </Card>
      )}
      {step === "choose" && current?.preview && <ChooseStep orgId={orgId} run={current} />}
      {step === "run" && current && <RunStep orgId={orgId} run={current} onDisconnect={() => undefined} />}
      {step === "report" && current && <ReportStep orgId={orgId} run={current} onAgain={() => select(null)} />}
      {(runs.data?.items.length ?? 0) > 1 && (
        <Card title={t("integrations.migration.earlier")}>
          <div className="mig-runs">
            {runs.data!.items.map((r) => (
              <button key={r.id} className={cx("mig-run", r.id === current?.id && "current")} onClick={() => select(r.id)}>
                <span>{dates.dateTime(r.created_at)}</span>
                <RunBadge status={r.status} />
                <span className="muted small">
                  {t("integrations.migration.projectsCount", { count: r.totals.projects.imported + r.totals.projects.partial + r.totals.projects.updated })}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}
      <p className="small muted">
        <Users size={13} style={{ verticalAlign: -2 }} /> {t("integrations.migration.trademark")}
        {current?.credential_expires_at && current.connected ? ` ${t("integrations.migration.tokenDeletedBy", { date: dates.dateTime(current.credential_expires_at) })}` : ""}
      </p>
    </div>
  );
}

/** "Migrate from OpenSolar" as a card: Lux & billing › Plan & usage and Settings › Integrations › Import (a link
 * only: no request, so the pages it sits on load as before). */
export function MigrationEntry({ compact = false }: { compact?: boolean }) {
  const t = useT();
  const { can } = useOrg();
  const canEdit = can("integrations", "edit");
  return (
    <div className={cx("mig-entry", compact && "is-compact")} data-testid="migration-entry">
      <div style={{ minWidth: 0 }}>
        <b>{t("integrations.migration.page.title")}</b>
        <div className="muted small">{t("integrations.migration.entry.body")}</div>
      </div>
      {canEdit ? (
        <Link className="btn" to="/migrate/opensolar">
          {t("integrations.migration.page.title")} <DirIcon><ArrowRight size={15} /></DirIcon>
        </Link>
      ) : (
        <span className="muted small">{t("integrations.migration.entry.adminsOnly")}</span>
      )}
    </div>
  );
}

/** The imported summary as words: "7.2 kW · 18 × Qcells Q.PEAK … · 13.5 kWh battery". */
export function importSummaryLine(t: Translate, intl: IntlShape, s: S["ImportSummary"] | null | undefined, currency = "USD"): string {
  if (!s) return "";
  const parts: string[] = [];
  if (s.kw) parts.push(t("integrations.migration.summary.kw", { kw: intl.formatNumber(s.kw, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) }));
  const m = s.modules?.[0];
  if (m) parts.push(`${s.module_count ?? m.quantity ?? ""} × ${[m.manufacturer, m.model].filter(Boolean).join(" ")}`.trim());
  if (s.battery_kwh) parts.push(t("integrations.migration.summary.battery", { kwh: intl.formatNumber(s.battery_kwh, { maximumFractionDigits: 1, minimumFractionDigits: 1 }) }));
  if (s.price) parts.push(intl.formatNumber(s.price, { style: "currency", currency, maximumFractionDigits: 0 }));
  return parts.join(" · ");
}
